/**
 * Find Us Section - Store Locator with Interactive Map
 * Handles Leaflet map initialization, markers, and store interactions
 */

class FindUsSection {
  constructor(container) {
    this.container = container;
    this.mapElement = container.querySelector('[id^="find-us-map-"]');
    this.storeList = container.querySelector('[id^="store-list-"]');
    this.storeItems = container.querySelectorAll('.find-us__store-item');
    
    if (!this.mapElement) {
      return;
    }
    
    this.sectionId = this.mapElement.dataset.sectionId;
    this.stores = this.parseStoresData();
    
    this.map = null;
    this.markers = [];
    
    this.init();
  }

  parseStoresData() {
    console.log('🔍 Parsing stores data...');
    try {
      // Try to get data from script tag first (new method)
      const scriptElement = document.getElementById(`stores-data-${this.sectionId}`);
      
      if (scriptElement) {
        console.log('📜 Found script element with JSON data');
        const storesData = scriptElement.textContent;
        console.log('📝 Raw stores data from script:', storesData);
        console.log('📏 Data length:', storesData ? storesData.length : 'null');
        
        const parsed = JSON.parse(storesData);
        console.log('✅ Successfully parsed stores from script:', parsed);
        
        // Log each store individually
        parsed.forEach((store, index) => {
          console.log(`🏪 Store ${index}:`, {
            id: store.id,
            name: store.name,
            lat: store.lat,
            lng: store.lng,
            latType: typeof store.lat,
            lngType: typeof store.lng
          });
        });
        
        return parsed;
      }
      
      // Fallback to data attribute (old method)
      console.log('📦 Trying data attribute fallback...');
      const storesData = this.mapElement.dataset.stores;
      console.log('📝 Raw stores data from dataset:', storesData);
      console.log('📏 Data length:', storesData ? storesData.length : 'null');
      
      const parsed = JSON.parse(storesData);
      console.log('✅ Successfully parsed stores:', parsed);
      
      // Log each store individually
      parsed.forEach((store, index) => {
        console.log(`🏪 Store ${index}:`, {
          id: store.id,
          name: store.name,
          lat: store.lat,
          lng: store.lng,
          latType: typeof store.lat,
          lngType: typeof store.lng
        });
      });
      
      return parsed;
    } catch (error) {
      console.error('❌ Error parsing stores data:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Stack:', error.stack);
      return [];
    }
  }

  init() {
    console.log('🎬 Initializing FindUsSection...');
    
    if (typeof L === 'undefined') {
      console.error('❌ Leaflet library not loaded! Window.L:', window.L);
      return;
    }
    console.log('✅ Leaflet library loaded. Version:', L.version);

    this.initMap();
    this.initStoreListeners();
    this.initMobileToggle();
  }

  initMap() {
    // Calculate center point from all stores
    const center = this.calculateCenter();
    
    // Initialize map
    this.map = L.map(this.mapElement.id, {
      center: [center.lat, center.lng],
      zoom: 6,
      scrollWheelZoom: false,
      dragging: true,
      touchZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      zoomControl: true
    });

    // Add tile layer (CartoDB Positron for clean, light style)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20
    }).addTo(this.map);

    // Add markers for each store
    this.addMarkers();

    // Fit bounds to show all markers
    if (this.markers.length > 0) {
      const group = L.featureGroup(this.markers);
      this.map.fitBounds(group.getBounds().pad(0.1));
    }

    // Fix for map tiles not loading properly
    setTimeout(() => {
      this.map.invalidateSize();
    }, 100);
  }

  calculateCenter() {
    if (this.stores.length === 0) {
      return { lat: 48.8566, lng: 2.3522 }; // Default to Paris
    }

    const sum = this.stores.reduce((acc, store) => {
      acc.lat += parseFloat(store.lat);
      acc.lng += parseFloat(store.lng);
      return acc;
    }, { lat: 0, lng: 0 });

    return {
      lat: sum.lat / this.stores.length,
      lng: sum.lng / this.stores.length
    };
  }

  addMarkers() {
    // Create custom marker icon
    const markerIcon = L.divIcon({
      className: 'custom-marker-icon',
      html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="24" height="24">
        <path fill-rule="evenodd" d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z" clip-rule="evenodd" />
      </svg>`,
      iconSize: [24, 24],
      iconAnchor: [12, 24],
      popupAnchor: [0, -24]
    });

    this.stores.forEach((store, index) => {
      const lat = parseFloat(store.lat);
      const lng = parseFloat(store.lng);
      
      if (isNaN(lat) || isNaN(lng)) {
        return;
      }
      
      const marker = L.marker([lat, lng], {
        icon: markerIcon,
        title: store.name
      }).addTo(this.map);

      // Add click event to marker
      marker.on('click', () => {
        this.highlightStore(index);
        this.scrollToStore(index);
      });

      this.markers.push(marker);
    });
  }

  initStoreListeners() {
    this.storeItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        // Don't trigger if clicking on a link or button
        if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || e.target.closest('button')) {
          return;
        }
        
        this.highlightStore(index);
        this.zoomToStore(index);
      });
    });
  }

  initMobileToggle() {
    const toggleButtons = this.container.querySelectorAll('.find-us__toggle-btn');
    
    toggleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent store item click
        
        const parent = button.closest('.find-us__store-item');
        const contentType = button.dataset.toggle;
        const addressContent = parent.querySelector('[data-content="address"]');
        const hoursContent = parent.querySelector('[data-content="hours"]');
        const allButtons = parent.querySelectorAll('.find-us__toggle-btn');

        // Toggle active state
        allButtons.forEach(btn => {
          btn.classList.remove('active');
          btn.setAttribute('aria-pressed', 'false');
        });
        
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');

        // Show/hide content with mobile-only visibility
        if (window.innerWidth < 990) {
          if (contentType === 'address') {
            addressContent.style.display = 'flex';
            hoursContent.style.display = 'none';
          } else {
            addressContent.style.display = 'none';
            hoursContent.style.display = 'flex';
          }
        }
      });
    });

    // Reset visibility on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth >= 990) {
          // Show all content on desktop
          this.container.querySelectorAll('[data-content]').forEach(content => {
            content.style.display = 'flex';
          });
        } else {
          // Reset to address view on mobile
          this.container.querySelectorAll('.find-us__store-item').forEach(item => {
            const addressContent = item.querySelector('[data-content="address"]');
            const hoursContent = item.querySelector('[data-content="hours"]');
            const addressBtn = item.querySelector('[data-toggle="address"]');
            const hoursBtn = item.querySelector('[data-toggle="hours"]');
            
            if (addressContent && hoursContent) {
              addressContent.style.display = 'flex';
              hoursContent.style.display = 'none';
              addressBtn?.classList.add('active');
              hoursBtn?.classList.remove('active');
            }
          });
        }
      }, 250);
    });

    // Initialize mobile toggle state
    if (window.innerWidth < 990) {
      this.container.querySelectorAll('.find-us__store-item').forEach(item => {
        const hoursContent = item.querySelector('[data-content="hours"]');
        if (hoursContent) {
          hoursContent.style.display = 'none';
        }
      });
    }
  }

  highlightStore(index) {
    // Remove highlight from all stores
    this.storeItems.forEach(item => {
      item.style.backgroundColor = '';
    });

    // Highlight selected store
    if (this.storeItems[index]) {
      this.storeItems[index].style.backgroundColor = 'rgba(var(--color-foreground), 0.04)';
      
      // Remove highlight after 2 seconds
      setTimeout(() => {
        this.storeItems[index].style.backgroundColor = '';
      }, 2000);
    }
  }

  scrollToStore(index) {
    if (this.storeItems[index]) {
      const headerOffset = 100;
      const elementPosition = this.storeItems[index].getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  zoomToStore(index) {
    if (this.markers[index]) {
      this.map.setView(
        this.markers[index].getLatLng(),
        13,
        { animate: true, duration: 0.5 }
      );
    }
  }
}

// Initialize all Find Us sections
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.find-us-section');
  sections.forEach(section => {
    new FindUsSection(section);
  });
});

// Handle Shopify theme editor events
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.find-us-section');
    if (section) {
      new FindUsSection(section);
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    // Reinitialize all sections after reorder
    setTimeout(() => {
      const sections = document.querySelectorAll('.find-us-section');
      sections.forEach(section => {
        new FindUsSection(section);
      });
    }, 100);
  });
}
